/**
 * What each connector capability lets okthx do, and which capabilities are only
 * useful together.
 *
 * Two readers share this one source:
 *  - the block message, shown when okthx cannot do what the user just asked
 *  - the connections screen, showing what okthx can and cannot do today
 *
 * This file carries meaning only. Which providers grant a capability is derived
 * from `providerCapabilities` in `api/src/crud/connectors.ts`, which stays the
 * single source of truth for that mapping — a second copy here would be free to
 * drift, and the drift would stay invisible until a user was told to connect
 * the wrong service.
 *
 * `login` and `profile:read` are deliberately absent: they are how an account
 * exists, not something a user chooses to unlock. `mcp:call` and `openapi:call`
 * are absent too, because what they enable is defined by the individual server
 * or spec, so no fixed copy here could describe them.
 */

/**
 * Whether a Google or Outlook sign-up ends at the profile grant.
 *
 * On, a new user is signed in as soon as the profile round has created their
 * account, and goes to the phone step and then into the product, as a phone
 * sign-up does. Access is asked for later, when something needs it. Off, the
 * profile round hands a new user on to the access round exactly as before.
 *
 * Evaluated before the session exists, so it is a global switch rather than
 * something that can be targeted or rolled out by percentage — see
 * `checkSignupGate`. Fails closed: an unrecognised gate reads false, which is
 * sign-up as it was.
 */
export const SIGNUP_STRAIGHT_IN_GATE = "signup-straight-in";

export const CAPABILITY_IDS = [
  "email:read",
  "email:send",
  "calendar:read",
  "calendar:write",
  "contact:read",
  "message:read",
  "message:send",
  "file:read",
  "file:write",
  "video:create",
] as const;

export type CapabilityId = (typeof CAPABILITY_IDS)[number];

/**
 * Where an account sits on the two axes that describe it.
 *
 * The axes are independent, and each needs its own word — one word for both
 * cannot express three of the four combinations. `authentication` answers how
 * the user signs in; `connections` answers what okthx can reach.
 */
export type AccountShape = {
  readonly authentication: "oauth" | "passwordless";
  readonly connections: "connected" | "unconnected";
};

/**
 * Every capability that reaches something of the user's.
 *
 * Deliberately not `CAPABILITY_IDS`. That list is the set this file can write
 * user-facing copy for, and it excludes `mcp:call` and `openapi:call` precisely
 * because their reach is defined by the individual server or spec. Reach is the
 * question here, and both of them have it — an account whose only connector is
 * an MCP server is connected — so borrowing the copy list would classify a
 * connected account as unconnected.
 *
 * Link's payment capability reaches the user's saved wallet methods, so a
 * wallet-only account is connected even though payments have no catalog copy.
 *
 * The only capabilities left out are `login` and `profile:read`: they say the
 * account exists, not that okthx can reach anything through it.
 *
 * A capability added to `providerCapabilities` and to neither list is a drift
 * this file cannot see; `accountShape.test.ts` fails on it.
 */
export const DATA_CAPABILITY_IDS = [
  ...CAPABILITY_IDS,
  "mcp:call",
  "openapi:call",
  "payment:request",
] as const;

/**
 * Whether a capability is one that reaches the user's data.
 *
 * This is why the connection state is derived from capabilities rather than
 * from the presence of a connector — an OAuth account that granted nothing
 * holds a connector and still reaches no data.
 */
export function isDataCapability(capability: string): boolean {
  return (DATA_CAPABILITY_IDS as readonly string[]).includes(capability);
}

/**
 * The capabilities a first-party catalog item needs before it reads as
 * connected, by catalog id. A connector serves the item only when it holds all
 * of them.
 *
 * Each one is what the item's card promises: "Connect your Google Calendar"
 * needs `calendar:read`, not any Google grant. The cost of an error decides the
 * direction. A Connected row offers no way to connect, so a false "Connected"
 * is a dead end, while a false "Connect" costs one extra consent screen.
 *
 * Here rather than on the catalog entries because the clients apply the same
 * rule to their Connected section, and this directory is the one they share.
 * An item with no entry is served by any connector of its provider.
 */
export const CATALOG_ITEM_CAPABILITIES: Readonly<
  Record<string, readonly (typeof DATA_CAPABILITY_IDS)[number][]>
> = {
  "provider:google": ["calendar:read"],
  "provider:microsoft": ["calendar:read"],
  "provider:slack": ["message:read", "message:send"],
  "provider:teams": ["message:read"],
  "provider:gdrive": ["file:read"],
  "provider:zoom": ["video:create"],
  "provider:link": ["payment:request"],
};

/**
 * The catalog item a connector fills: a mailbox connected for mail alone fills
 * its provider's inbox item, and every other connector fills the provider item.
 *
 * Shared by the server, which says whether the connector serves this item, and
 * the clients, which list the connector under it.
 */
export function connectorCatalogItemId(connector: {
  provider: string;
  isLogin: boolean;
  supportsEmail: boolean;
}): string {
  const isEmailInbox = !connector.isLogin && connector.supportsEmail;
  return `${isEmailInbox ? "email_inbox" : "provider"}:${connector.provider}`;
}

/**
 * Whether a connector with these capabilities serves a catalog item.
 *
 * A connector that records no capabilities serves it. The column is nullable
 * and older rows were never backfilled, so an empty record is one we cannot
 * classify, not one that reaches nothing. Reading it as unconnected would tell
 * a user their working service is disconnected.
 */
export function connectorServesCatalogItem(
  catalogItemId: string,
  capabilities: readonly string[] | null | undefined,
): boolean {
  if (!capabilities || capabilities.length === 0) return true;
  const required = CATALOG_ITEM_CAPABILITIES[catalogItemId] ?? [];
  return required.every((capability) => capabilities.includes(capability));
}

/**
 * The account's shape, from the union of its connectors' capabilities.
 *
 * One predicate covers both unconnected cells. A passwordless account holds no
 * data capability because it has no connector; an OAuth account can hold none
 * because the user granted none. A predicate written against connector rows
 * would answer only the first.
 *
 * Pure, and defined here rather than beside the database code, so the rig
 * module that reports account shape and the product that acts on it read the
 * same function and cannot drift apart.
 */
export function accountShapeFromCapabilities(capabilities: readonly string[]): AccountShape {
  return {
    authentication: capabilities.includes("login") ? "oauth" : "passwordless",
    connections: capabilities.some(isDataCapability) ? "connected" : "unconnected",
  };
}

/** True when okthx can reach none of the user's data. */
export function isUnconnected(capabilities: readonly string[]): boolean {
  return accountShapeFromCapabilities(capabilities).connections === "unconnected";
}

export type CapabilityInfo = {
  readonly id: CapabilityId;
  /** Short noun for the access itself, as the user would name it. */
  readonly title: string;
  /**
   * What okthx does with the data, stated plainly. This is consent copy, so it
   * describes the access rather than selling the benefit — a cautious user has
   * to be able to read this screen and believe it. The benefit belongs in
   * `enablesAlone` and in the combinations below.
   */
  readonly grants: string;
  /**
   * What this capability makes possible on its own, or `null` when it makes
   * nothing possible on its own. `calendar:write` is the clearest `null`:
   * okthx cannot place an event until something can read a calendar and find
   * the time.
   *
   * Named for the constraint as well as the content, because the constraint is
   * the axis this file turns on: everything a capability does *with* a partner
   * lives in `CAPABILITY_COMBINATIONS` instead.
   */
  readonly enablesAlone: string | null;
  /**
   * What okthx asks the user for when this capability is missing, in place of
   * looking it up.
   *
   * Asking is the right degradation, and it is why this field exists rather
   * than one describing a partial view. okthx keeps its own calendar and learns
   * people from messages that pass through it, so it could answer from what it
   * did itself — and that answer can be confidently wrong, proposing a time
   * against a meeting it cannot see. A question cannot be wrong that way.
   *
   * It is also where the upsell belongs. The user has just supplied by hand
   * something a connected service would have supplied for them, so the offer
   * lands on a cost they felt a second ago. That rule is general: whenever
   * okthx asks for something a capability would have answered, it names the
   * service. This field carries the copy for the cases worth wording; the rule
   * covers the rest and lives in the prompt.
   *
   * Absent where asking makes no sense. Nobody can paste their inbox.
   */
  readonly asksInstead?: string;
};

export const CAPABILITY_INFO: Record<CapabilityId, CapabilityInfo> = {
  "email:read": {
    id: "email:read",
    title: "Your email",
    grants: "Reads the mail in your mailbox, including message bodies and attachments.",
    enablesAlone: "Okthx can tell you what arrived, group it, and say what still needs an answer.",
  },
  "email:send": {
    id: "email:send",
    title: "Sending email as you",
    grants: "Sends mail from your address and saves drafts in your mailbox.",
    enablesAlone: "Your mail goes out from your own address instead of from Okthx's address.",
  },
  "calendar:read": {
    id: "calendar:read",
    title: "Your calendar",
    grants: "Reads your events, including their times, titles, locations, and guests.",
    enablesAlone: "Okthx knows when you are free and what your day holds.",
    asksInstead: "Which times work for you?",
  },
  "calendar:write": {
    id: "calendar:write",
    title: "Changing your calendar",
    grants: "Creates, moves, and cancels events on your calendar, and answers invitations.",
    enablesAlone: null,
  },
  "contact:read": {
    id: "contact:read",
    title: "Your contacts",
    grants: "Reads your saved contacts — their names, addresses, and phone numbers.",
    enablesAlone: "You can name a person instead of typing their address.",
    asksInstead: "What is their email address?",
  },
  "message:read": {
    id: "message:read",
    title: "Your chat messages",
    grants: "Reads the direct messages people send you in Slack, Teams, or WhatsApp.",
    enablesAlone: "Okthx can tell you what you missed in chat.",
  },
  "message:send": {
    id: "message:send",
    title: "Sending chat as you",
    grants: "Sends direct messages as you in Slack, Teams, or WhatsApp.",
    enablesAlone: "Okthx can answer a chat message on your behalf.",
  },
  "file:read": {
    id: "file:read",
    title: "Your files",
    grants: "Searches and reads the files in your drive.",
    enablesAlone: "Okthx can find a document and answer from what is inside it.",
    asksInstead: "Can you paste the document, or attach it?",
  },
  "file:write": {
    id: "file:write",
    title: "Saving files",
    grants: "Creates and updates files in your drive.",
    enablesAlone: "Okthx saves what it writes to your drive instead of leaving it in a thread.",
  },
  "video:create": {
    id: "video:create",
    title: "Video meeting links",
    grants: "Creates meeting links on your video account.",
    enablesAlone: null,
  },
};

/**
 * The whole of what okthx says about availability when the only calendar it can
 * read is its own — times, limitation and question in one string.
 *
 * ONE STRING IS THE MECHANISM, not a formatting choice. An account with no
 * `calendar:read` still has a calendar: okthx keeps one, and it holds every
 * meeting okthx booked. Two weaker shapes were tried against a live agent and
 * both failed:
 *
 *   - Withhold the times. The agent said it could not help at all, throwing away
 *     the meetings okthx had booked itself — and okthx then proposes over them,
 *     the collision `okthxKindBlocks` exists to prevent, for exactly the users
 *     with no other calendar to check against.
 *   - Return the times beside a caveat field. The agent reported the times and
 *     dropped the caveat, which is the original failure with real numbers behind
 *     it: a confident, structured, wrong answer the user acts on by booking.
 *
 * Welded, neither is available. The times do not exist anywhere except inside a
 * sentence that already carries what they do not cover and the question that has
 * to follow them.
 */
/**
 * What okthx actually knows about a window when it has no `calendar:read`.
 *
 * - `nothing-on-record` — okthx has no commitment in the window at all.
 * - `some-times` — okthx knows of commitments and found gaps between them.
 * - `all-taken` — okthx knows of commitments and they fill the window.
 *
 * KNOWN COMMITMENTS DECIDE FIRST, and the order is the whole point. Free time is
 * computed by inverting busy periods, so an okthx calendar holding nothing
 * inverts to the entire window being free — "okthx knows of nothing" arrives
 * looking exactly like "everything is open". Branching on the times first
 * reproduced the original bug inside the function that used to do it: a rig run
 * was handed "these times look open: 12:00 to 18:00" derived from an empty
 * calendar, and only the agent's own judgement stopped it reaching the user.
 *
 * Times are therefore only worth reporting when okthx knows of at least one
 * commitment, because only then has it actually looked at anything.
 *
 * Shared by every message built on this distinction so the ordering cannot drift
 * between them.
 */
export function okthxOnlyState(
  formattedTimes: readonly string[],
  knownCommitments: number,
): "nothing-on-record" | "some-times" | "all-taken" {
  if (knownCommitments === 0) return "nothing-on-record";
  return formattedTimes.length > 0 ? "some-times" : "all-taken";
}

/**
 * What to tell the agent when a PROPOSAL was built without calendar access —
 * times about to be put in an email to a participant, in the user's name.
 *
 * Distinct from {@link okthxOnlyAvailabilityMessage}, which answers the user's
 * own question about their availability. The difference is who reads the answer
 * and what it costs to be wrong: the user can push back on a number they know is
 * shaky, while a participant reads it as a statement of when the user is free
 * and books around it. So this one does not offer to proceed — it asks the user
 * to settle the times first, and names how to carry on once they have.
 */
export function okthxOnlyProposalMessage(
  formattedTimes: readonly string[],
  knownCommitments: number,
): string {
  const [opening, ask] = (() => {
    switch (okthxOnlyState(formattedTimes, knownCommitments)) {
      case "nothing-on-record":
        return [
          "Tell the user Okthx has nothing at all on record for that window — not that they are free, only that Okthx knows of nothing. Do not present any times as open, and do not offer them to the participant.",
          "Then ask the user which times to propose.",
        ];
      case "some-times":
        return [
          `Tell the user that, going only on what Okthx scheduled itself, these look open: ${formattedTimes.join("; ")}.`,
          "Then ask the user to confirm those times before they go to the participant, or to give different ones.",
        ];
      case "all-taken":
        return [
          "Tell the user that every part of that window is taken by something Okthx scheduled itself, so it has nothing to propose.",
          "Then ask the user which times to offer instead.",
        ];
    }
  })();

  return [
    opening,
    "Then say plainly what that does not cover: Okthx cannot see the user's real calendars, because no calendar account is connected, so anything booked elsewhere is invisible to it.",
    ask,
    "Do NOT draft or send a kickoff until the user answers — these times would go to the participant as the user's availability. Once they give or confirm times, set meetingDetails.skipCalendarConflicts and search again to proceed with what they chose.",
  ].join(" ");
}

export function okthxOnlyAvailabilityMessage(
  formattedTimes: readonly string[],
  knownCommitments: number,
): string {
  // The ordering that makes the three states safe now lives in `okthxOnlyState`,
  // which explains why known commitments decide before the times do.
  //
  // EACH STATE CARRIES ITS OWN ASK. One shared closing asking the user to
  // "confirm the times" is incoherent in the two states that have no times to
  // confirm, and an agent handed it in the nothing-on-record state dropped the
  // question entirely and left the thread with nowhere to go. The ask is the
  // half that keeps the conversation alive, so it has to make sense wherever it
  // lands.
  const [opening, ask] = (() => {
    switch (okthxOnlyState(formattedTimes, knownCommitments)) {
      case "nothing-on-record":
        return [
          "Tell the user that Okthx has nothing at all on record for that window — not that they are free, only that Okthx knows of nothing. Do not present any times as open.",
          "Then ask the user what times work for them, so the conversation can carry on without a calendar.",
        ];
      case "some-times":
        return [
          `Tell the user these times look open: ${formattedTimes.join("; ")}.`,
          "Then ask the user to confirm those times are actually free. Okthx cannot check that itself, so do not treat any of them as available until the user says so.",
        ];
      case "all-taken":
        return [
          "Tell the user that every part of that window is taken by something Okthx scheduled itself.",
          "Then ask the user whether anything else should be worked around, since Okthx can only see what it booked.",
        ];
    }
  })();

  return [
    opening,
    "Then say plainly what that does not cover: these come only from what Okthx has scheduled itself, because no calendar account is connected, so Okthx cannot see anything booked anywhere else.",
    ask,
  ].join(" ");
}

export type CapabilityCombination = {
  readonly id: string;
  /** Every capability the user must hold. Order is not significant. */
  readonly requires: readonly CapabilityId[];
  readonly title: string;
  /** What the set does that no member of it does alone. */
  readonly description: string;
  /**
   * Stable key for an optional demo clip. The asset lives with the client, so a
   * clip nobody has made yet is a missing file rather than a broken record here.
   */
  readonly demoKey?: string;
};

/**
 * Ordered from the cheapest to earn to the most expensive, which is also the
 * order to offer them in: a user is never shown a three-capability combination
 * ahead of a two-capability one they are closer to completing.
 */
export const CAPABILITY_COMBINATIONS: readonly CapabilityCombination[] = [
  {
    id: "answer-your-mail",
    requires: ["email:read", "email:send"],
    title: "Answer your mail",
    description:
      "Okthx reads what arrived and writes the reply, so a thread can be finished without you opening it.",
    demoKey: "answer-your-mail",
  },
  {
    id: "reach-people-by-name",
    requires: ["contact:read", "email:send"],
    title: "Reach people by name",
    description: "You can say “email Dana” instead of looking up an address.",
  },
  {
    id: "manage-your-day",
    requires: ["calendar:read", "calendar:write"],
    title: "Manage your day",
    description:
      "Okthx moves meetings, holds focus time, and adds travel buffers, instead of only telling you about a conflict.",
    demoKey: "manage-your-day",
  },
  {
    id: "schedule-with-people",
    requires: ["calendar:read", "email:send"],
    title: "Schedule with anyone",
    description:
      "Okthx proposes times that genuinely work and settles them by email with people who do not use Okthx.",
    demoKey: "schedule-with-people",
  },
  {
    id: "briefings-that-know-your-day",
    requires: ["email:read", "calendar:read"],
    title: "Briefings that know your day",
    description:
      "Okthx tells you what needs an answer before your next meeting, rather than only what is unread.",
  },
  {
    id: "know-who-matters",
    requires: ["email:read", "contact:read"],
    title: "Know who you work with",
    description:
      "Okthx builds a picture of the people you exchange mail with, so it can brief you on someone and make introductions.",
  },
  {
    id: "file-what-arrives",
    requires: ["email:read", "file:write"],
    title: "File what arrives",
    description:
      "Okthx saves an attachment into your drive as it lands, under a name you can find again.",
  },
  {
    id: "answer-from-your-documents",
    requires: ["email:read", "file:read"],
    title: "Answer from your documents",
    description: "Okthx pulls the right file into a reply instead of asking you to go and find it.",
  },
  {
    id: "meetings-with-a-link",
    requires: ["calendar:write", "video:create"],
    title: "Meetings with a link",
    description: "Every meeting Okthx books carries a video link without you adding one.",
  },
  {
    id: "scheduling-end-to-end",
    requires: ["calendar:read", "calendar:write", "email:send"],
    title: "Scheduling, end to end",
    description:
      "The agreed time lands on your calendar and the invitation goes out, with nothing handed back to you.",
    demoKey: "scheduling-end-to-end",
  },
  {
    id: "handle-scheduling-in-chat",
    requires: ["message:read", "message:send", "calendar:read"],
    title: "Handle scheduling in chat",
    description:
      "Okthx answers “when are you free?” in Slack or Teams without moving the conversation to email.",
  },
  {
    id: "full-pre-meeting-brief",
    requires: ["email:read", "calendar:read", "contact:read"],
    title: "A full pre-meeting brief",
    description:
      "Before a meeting Okthx tells you who you are meeting, what you last discussed, and what is still open.",
    demoKey: "full-pre-meeting-brief",
  },
];

/**
 * What okthx does for an account with nothing connected. The connections screen
 * shows this first: someone deciding whether to grant anything needs to see
 * that the product already works, or every capability above reads as a paywall.
 */
export const WITHOUT_ANY_CONNECTOR: readonly { title: string; description: string }[] = [
  {
    title: "Phone calls",
    description: "Okthx calls a restaurant, a clinic, or a business for you, and reports back.",
  },
  {
    title: "Messaging through Okthx",
    description: "Okthx sends email and texts on your behalf from its own address and number.",
  },
  { title: "Reminders", description: "Okthx holds reminders on its own calendar and tells you." },
  {
    title: "Research",
    description: "Okthx searches the web and reads pages to answer a question.",
  },
];

/** True when every capability in `required` is held. */
export function hasAllCapabilities(
  held: ReadonlySet<CapabilityId>,
  required: readonly CapabilityId[],
): boolean {
  return required.every((id) => held.has(id));
}

/** Every combination the user can already use. */
export function availableCombinations(
  held: Iterable<CapabilityId>,
): readonly CapabilityCombination[] {
  const set = new Set(held);
  return CAPABILITY_COMBINATIONS.filter((c) => hasAllCapabilities(set, c.requires));
}

/**
 * The combinations that adding `candidate` would complete.
 *
 * This is the multiplier message, and it is why combinations are modelled apart
 * from capabilities. The interesting claim at an upsell is rarely what the new
 * capability does by itself — it is what the new capability *finishes*, given
 * everything the user already connected.
 *
 * `candidate` takes a set as well as one id, because a connector is what a user
 * actually adds and a connector grants several capabilities at once: offering
 * Google one capability at a time would undercount what connecting it does.
 * Returns empty when everything offered is already held.
 */
export function combinationsUnlockedBy(
  held: Iterable<CapabilityId>,
  candidate: CapabilityId | readonly CapabilityId[],
): readonly CapabilityCombination[] {
  const set = new Set(held);
  const after = new Set(set);
  for (const id of typeof candidate === "string" ? [candidate] : candidate) after.add(id);
  if (after.size === set.size) return [];
  return CAPABILITY_COMBINATIONS.filter(
    (c) => hasAllCapabilities(after, c.requires) && !hasAllCapabilities(set, c.requires),
  );
}

/** The capabilities still missing before `combination` can be used. */
export function missingForCombination(
  combination: CapabilityCombination,
  held: Iterable<CapabilityId>,
): readonly CapabilityId[] {
  const set = new Set(held);
  return combination.requires.filter((id) => !set.has(id));
}
